import React from 'react';
import playbackStopChangelog from './playback-stop.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  PlaybackStopNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Playback Stop',
  identifier: 'PlaybackStop',
  command_id: 'call.playback.ended',
  icon: 'telnyx',
  background: '#000000',
  changelog: playbackStopChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.playback.ended.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.playback.ended.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      id: 'call.playback.ended',
      label: 'call.playback.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.playback.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      id: 'call.speak.ended',
      label: 'call.speak.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.speak.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.playback.ended',
      expected: false,
      multi: false,
    },
    {
      webhook: 'call.speak.ended',
      expected: false,
      multi: false,
    },
  ],
};

export const state: PlaybackStopNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  stop: { type: NodeInputType.Option, value: '' },
  overlay: { type: NodeInputType.Option, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'overlay',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Overlay',
    description: 'When enabled, it stops the audio being played in the overlay queue.',
    required: false,
    placeholder: 'Stop audio in overlay queue.',
    options: [
      { label: 'None', value: '' },
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
  {
    id: 'stop',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Stop Audio',
    description:
      'When specified, it stops the current audio being played. Specify current to stop the current audio being played, and to play the next file in the queue. Specify all to stop the current audio file being played and to also clear all audio files from the queue.',
    required: false,
    placeholder: 'Specify to stop audio before speaking',
    options: [
      { label: 'None', value: '' },
      { label: 'Current', value: 'current' },
      { label: 'All', value: 'all' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.speak.ended': {
      call: {
        speak: {
          ended: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            status: 'string',
          },
        },
      },
    },
    'call.playback.ended': {
      call: {
        playback: {
          ended: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            media_url: 'string',
            media_name: 'string',
            overlay: 'boolean',
            status: 'string',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const PlaybackStop: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.playback.ended') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
